/**
 * Phase 3 — Canonical service key mapping
 *
 * These keys MUST match the `services.service_key` values in the database.
 * Used by frontend hooks and backend guards for consistent references.
 */

export const SERVICE_KEYS = {
  JOB_MANAGEMENT: 'job_management',
  CANDIDATE_PIPELINE: 'candidate_pipeline',
  AI_CV_SCORING: 'ai_cv_scoring',
  AI_INTERVIEWS: 'ai_interviews',
  AI_JOB_GENERATOR: 'ai_job_generator',
  TALENT_POOL: 'talent_pool',
  ANALYTICS: 'analytics',
  TEAM_MANAGEMENT: 'team_management',
  INTERVIEW_RETAKES: 'interview_retakes',
  JOB_DISCOVERY: 'job_discovery',
  INTERVIEW_PRACTICE: 'interview_practice',
  CV_REVIEW: 'cv_review',
  CV_BUILDER: 'cv_builder',
} as const;

export type ServiceKey = (typeof SERVICE_KEYS)[keyof typeof SERVICE_KEYS];

/** Human-friendly labels for each service key (shown in admin UI) */
export const SERVICE_KEY_LABELS: Record<ServiceKey, string> = {
  [SERVICE_KEYS.JOB_MANAGEMENT]: 'Job Management',
  [SERVICE_KEYS.CANDIDATE_PIPELINE]: 'Candidate Pipeline',
  [SERVICE_KEYS.AI_CV_SCORING]: 'AI CV Scoring',
  [SERVICE_KEYS.AI_INTERVIEWS]: 'AI Interviews',
  [SERVICE_KEYS.AI_JOB_GENERATOR]: 'AI Job Generator',
  [SERVICE_KEYS.TALENT_POOL]: 'Talent Discovery',
  [SERVICE_KEYS.ANALYTICS]: 'Analytics & Reports',
  [SERVICE_KEYS.TEAM_MANAGEMENT]: 'Team Management',
  [SERVICE_KEYS.INTERVIEW_RETAKES]: 'Interview Retakes',
  [SERVICE_KEYS.JOB_DISCOVERY]: 'Job Discovery',
  [SERVICE_KEYS.INTERVIEW_PRACTICE]: 'Interview Practice',
  [SERVICE_KEYS.CV_REVIEW]: 'CV Review',
  [SERVICE_KEYS.CV_BUILDER]: 'CV Builder',
};

/** All possible service key values as an array */
export const ALL_SERVICE_KEYS = Object.values(SERVICE_KEYS);

/** ForkSeek paid/credit-backed service keys. Browsing/basic editing can remain free at feature level. */
export const FORKSEEK_SERVICE_KEYS = [
  SERVICE_KEYS.JOB_DISCOVERY,
  SERVICE_KEYS.INTERVIEW_PRACTICE,
  SERVICE_KEYS.CV_REVIEW,
  SERVICE_KEYS.CV_BUILDER,
] as const;

/** Maps platform routes/sections to their service key */
export const ROUTE_SERVICE_MAP: Record<string, ServiceKey> = {
  '/jobs': SERVICE_KEYS.JOB_MANAGEMENT,
  '/candidates': SERVICE_KEYS.CANDIDATE_PIPELINE,
  '/interviews': SERVICE_KEYS.AI_INTERVIEWS,
  '/talent': SERVICE_KEYS.TALENT_POOL,
  '/talent/invitations': SERVICE_KEYS.TALENT_POOL,
  '/analytics': SERVICE_KEYS.ANALYTICS,
  '/team': SERVICE_KEYS.TEAM_MANAGEMENT,
};
